import { useEffect, useMemo, useState } from "react";
import { hotSearch, type HotRankItem } from "@/mocks/hotData";

const groupMap: Record<string, string> = {
  娱乐: "娱乐瓜",
  文娱: "娱乐瓜",
  社会: "社会热点",
  民生: "社会热点",
  科技: "科技热闻",
  生活: "生活潮流",
  校园: "生活潮流",
};

const filters = ["全部", "娱乐瓜", "社会热点", "科技热闻", "生活潮流"];

export default function HotSearch() {
  const [view, setView] = useState<"heat" | "time">("heat");
  const [cat, setCat] = useState("全部");
  const [focusTitle, setFocusTitle] = useState("");

  useEffect(() => {
    const saved = sessionStorage.getItem("chigua_focus");
    if (saved) {
      setFocusTitle(saved);
      sessionStorage.removeItem("chigua_focus");
      window.setTimeout(() => {
        const el = document.querySelector(`[data-row="${saved}"]`);
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 120);
    }
  }, []);

  const maxHeat = hotSearch[0]?.heat ?? 1;

  const rows: HotRankItem[] = useMemo(() => {
    let list = hotSearch;
    if (cat !== "全部") list = list.filter((i) => groupMap[i.category] === cat);
    const sorted = [...list];
    if (view === "heat") sorted.sort((a, b) => b.heat - a.heat);
    return sorted;
  }, [view, cat]);

  const deltaView = (d: number) => {
    if (d > 0)
      return (
        <span className="flex items-center gap-0.5 text-primary-600">
          <i className="ri-arrow-up-line"></i>
          {d}
        </span>
      );
    if (d < 0)
      return (
        <span className="flex items-center gap-0.5 text-accent-600">
          <i className="ri-arrow-down-line"></i>
          {Math.abs(d)}
        </span>
      );
    return <span className="flex items-center gap-0.5 text-foreground-400">新</span>;
  };

  return (
    <section id="hot" className="mx-auto max-w-[1200px] scroll-mt-24 px-4 py-16 md:px-6 md:py-20">
      <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-100 text-primary-700">
              <i className="ri-fire-fill text-xl"></i>
            </span>
            <h2 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">全网热搜榜</h2>
          </div>
          <p className="mt-3 max-w-xl text-sm text-foreground-600">
            综合全网搜索与讨论热度实时汇总，切到"时间"视图即可看到最近发酵的新鲜瓜点。
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex flex-wrap gap-1.5">
            {filters.map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setCat(f)}
                className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors ${
                  cat === f
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-secondary-100"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
          <div className="flex shrink-0 rounded-full bg-background-100 p-1">
            {(["heat", "time"] as const).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setView(v)}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${
                  view === v ? "bg-foreground-950 text-background-50" : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                {v === "heat" ? "按热度" : "按时间"}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-4">
        {rows.map((item, idx) => {
          const rank = idx + 1;
          const active = item.title === focusTitle;
          const barW = Math.max(14, Math.round((item.heat / maxHeat) * 100));
          return (
            <div
              key={item.title}
              data-row={item.title}
              className={`group relative flex items-center gap-4 rounded-2xl border px-4 py-3.5 transition-all md:gap-6 md:px-5 ${
                active
                  ? "border-primary-400 bg-primary-50 ring-2 ring-primary-200"
                  : "border-background-200 bg-background-50 hover:border-primary-200 hover:bg-background-100/60"
              }`}
            >
              <span
                className={`w-8 shrink-0 text-center font-heading text-xl font-black md:text-2xl ${
                  rank <= 3 ? "text-primary-600" : "text-foreground-300"
                }`}
              >
                {String(rank).padStart(2, "0")}
              </span>

              <span className="w-10 shrink-0 text-foreground-400">{deltaView(item.delta)}</span>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="truncate text-sm font-semibold text-foreground-950 md:text-base">{item.title}</h3>
                  <span className="whitespace-nowrap rounded bg-secondary-100 px-1.5 py-0.5 text-[11px] text-secondary-800">
                    {item.tag}
                  </span>
                </div>
                <div className="mt-2 hidden h-1.5 w-40 overflow-hidden rounded-full bg-background-200 sm:block">
                  <div
                    className={`h-full rounded-full ${idx < 3 ? "bg-primary-500" : "bg-accent-400"}`}
                    style={{ width: `${barW}%` }}
                  ></div>
                </div>
              </div>

              <div className="hidden shrink-0 items-center gap-1 text-sm font-semibold text-primary-600 sm:flex">
                <i className="ri-fire-line"></i>
                {item.heat}
              </div>
              <a
                href="#event"
                className="shrink-0 whitespace-nowrap text-xs text-foreground-500 transition-colors group-hover:text-primary-700"
              >
                去跟瓜
                <i className="ri-arrow-right-line ml-0.5"></i>
              </a>
            </div>
          );
        })}
      </div>
    </section>
  );
}